import { useState, useCallback } from "react";
import * as api from "../../api";

/* PASO 4 (F3B) — Banners promocionales (admin) + banner activo (cliente).
 * Código movido VERBATIM desde App.js — misma semántica, mismos toasts. */
export function useAdminBanners(showToast) {
  const [banners, setBanners] = useState([]);
  const [bannerForm, setBannerForm] = useState({
    id: null, game_id: "", title: "", description: "", images: [], is_active: false, fire_effect: false,
  });
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerUploadingIdx, setBannerUploadingIdx] = useState(false);
  const [activeGameBanner, setActiveGameBanner] = useState(null);

  const loadBanners = useCallback(async () => {
    try {
      const list = await api.adminListBanners();
      setBanners(list);
    } catch (err) {
      showToast("Error", err.message || "No se pudieron cargar los banners", "error");
    }
  }, [showToast]);

  const loadActiveGameBanner = useCallback(async (gameId) => {
    if (!gameId) { setActiveGameBanner(null); return; }
    try {
      const doc = await api.getActiveBanner(gameId);
      setActiveGameBanner(doc && doc.images && doc.images.length ? doc : null);
    } catch (e) {
      console.warn("active banner load failed:", e);
      setActiveGameBanner(null);
    }
  }, []);

  const resetBannerForm = () => setBannerForm({
    id: null, game_id: "", title: "", description: "", images: [], is_active: false, fire_effect: false,
  });

  const handleBannerImageUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      showToast("Formato inválido", "Solo imágenes", "error"); return;
    }
    if (file.size > 4 * 1024 * 1024) {
      showToast("Imagen muy grande", "Máximo 4 MB por imagen", "error"); return;
    }
    if (bannerForm.images.length >= 5) {
      showToast("Límite alcanzado", "Máximo 5 imágenes por banner", "error"); return;
    }
    setBannerUploadingIdx(true);
    try {
      const up = await api.uploadSupportScreenshot(file);
      if (up?.url) {
        setBannerForm(f => ({ ...f, images: [...f.images, up.url] }));
      }
    } catch (err) {
      showToast("Error", err.message || "No se pudo subir", "error");
    } finally {
      setBannerUploadingIdx(false);
    }
  };

  const handleRemoveBannerImage = (idx) => {
    setBannerForm(f => ({ ...f, images: f.images?.filter((_, i) => i !== idx) }));
  };

  const handleSaveBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.game_id) return showToast("Falta el juego", "Selecciona un juego", "error");
    if (!bannerForm.title.trim()) return showToast("Falta el título", "Título obligatorio", "error");
    setBannerBusy(true);
    try {
      const body = {
        title: bannerForm.title.trim(),
        description: bannerForm.description.trim() || null,
        images: bannerForm.images,
        is_active: !!bannerForm.is_active,
        fire_effect: !!bannerForm.fire_effect,
      };
      let saved;
      if (bannerForm.id) {
        saved = await api.adminUpdateBanner(bannerForm.id, body);
      } else {
        saved = await api.adminCreateBanner({ ...body, game_id: bannerForm.game_id });
      }
      showToast(bannerForm.id ? "Banner actualizado" : "Banner creado", saved.title, "success");
      resetBannerForm();
      loadBanners();
    } catch (err) {
      showToast("Error", err.message || "No se pudo guardar", "error");
    } finally {
      setBannerBusy(false);
    }
  };

  const handleToggleBannerActive = async (banner) => {
    try {
      await api.adminUpdateBanner(banner.id, { is_active: !banner.is_active });
      loadBanners();
    } catch (err) {
      showToast("Error", err.message || "No se pudo actualizar", "error");
    }
  };

  const handleDeleteBanner = async (banner) => {
    if (!window.confirm(`¿Eliminar banner "${banner.title}"?`)) return;
    try {
      await api.adminDeleteBanner(banner.id);
      loadBanners();
    } catch (err) {
      showToast("Error", err.message || "No se pudo eliminar", "error");
    }
  };

  return {
    banners, bannerForm, setBannerForm, bannerBusy, bannerUploadingIdx,
    activeGameBanner, setActiveGameBanner,
    loadBanners, loadActiveGameBanner, resetBannerForm,
    handleBannerImageUpload, handleRemoveBannerImage, handleSaveBanner,
    handleToggleBannerActive, handleDeleteBanner,
  };
}
